/** Versions 1, 2 and 3: integer physics shared with night_flight/rules.py. */
export const VERSION = 3;
export const MAX_TICKS = 72000;
export const WIDTH = 360;
export const HEIGHT = 640;
export const initialState = () => ({ tick: 0, y: 320000, velocity: 0, distance: 0, score: 0, alive: true, last_flap: -6, collected: [] });

export function randomGate(seed, index, salt = 0) {
  const value = (seed + Math.imul(index, 2654435761) + salt) >>> 0;
  const mixed = (Math.imul(value, 1664525) + 1013904223) >>> 0;
  return (mixed ^ (mixed >>> 16)) >>> 0;
}

export function course(seed, version = VERSION) {
  if (![1, 2, 3].includes(version)) throw new Error("rules_version");
  let empty = 0;
  let center = 320000;
  let nextItem = 6 + randomGate(seed, 0, 401) % 7;
  return Array.from({ length: version === 1 ? 620 : version === 2 ? 1200 : 2000 }, (_, id) => {
    let item = false;
    if (id >= 4 && (id + 1) % 5 === 0) {
      item = empty === 3 || randomGate(seed, id, 917) % 100 < 35;
      empty = item ? 0 : empty + 1;
    }
    if (version >= 2) {
      item = id === nextItem;
      if (item) nextItem += 8 + randomGate(seed, id, 917) % 9;
    }
    const candidate = (170 + randomGate(seed, id) % 281) * 1000;
    center = Math.max(center - 90000, Math.min(center + 90000, candidate));
    const offset = version >= 3 ? id + 75 : id;
    const gap = version === 1 ? Math.max(160, 220 - Math.floor(id / 10) * 6) * 1000 : 220000 - Math.floor(120000 * offset / (offset + 100));
    const gate = { id, x: (400 + id * 230) * 1000, center,
      gap, item };
    if (version >= 2) {
      const margin = Math.floor(gap / 2) - 45000;
      gate.item_x = gate.x + (randomGate(seed, id, 1231) % 100001 - 50000);
      gate.item_y = center + randomGate(seed, id, 1777) % (2 * margin + 1) - margin;
    }
    return gate;
  });
}

export function flightSpeed(score, tick, version = VERSION) {
  if (version === 1) return 1400 + Math.min(5, Math.floor(tick / 900)) * 100;
  if (version === 2) return 1400 + Math.floor(1800 * score / (score + 120));
  const earlySteps = Math.floor(Math.min(score, 100) / 10);
  const lateSteps = Math.floor(Math.max(0, score - 100) / 25);
  return 1600 + 150 * earlySteps + Math.floor(2200 * lateSteps / (lateSteps + 12));
}

export function step(state, flap, gates, version = VERSION) {
  if (!state.alive || state.tick >= MAX_TICKS) return state;
  state.tick++;
  if (flap) {
    if (state.tick - state.last_flap < 6) throw new Error("rapid_input");
    state.velocity = -4400;
    state.last_flap = state.tick;
  }
  state.velocity = Math.min(7000, state.velocity + 210);
  state.y += state.velocity;
  state.distance += flightSpeed(state.score, state.tick, version);
  if (state.y < 14000 || state.y > 626000) { state.alive = false; return state; }
  const first = Math.max(0, Math.floor((state.distance - 400000) / 230000));
  const nearby = gates.slice(first, first + 3);
  for (const gate of nearby) {
    const x = gate.x - state.distance;
    if (x - 30000 < 104000 && x + 30000 > 76000 &&
        (state.y - 14000 < gate.center - Math.floor(gate.gap / 2) || state.y + 14000 > gate.center + Math.floor(gate.gap / 2))) {
      state.alive = false; return state;
    }
  }
  for (const gate of nearby) {
    const x = gate.x - state.distance;
    if (gate.item && !state.collected.includes(gate.id) &&
        ((gate.item_x ?? gate.x) - state.distance - 90000) ** 2 + (state.y - (gate.item_y ?? gate.center)) ** 2 <= 30000 ** 2) state.collected.push(gate.id);
  }
  while (state.score < gates.length && gates[state.score].x - state.distance + 30000 < 76000) state.score++;
  return state;
}

export function cloneState(state) { return { ...state, collected: [...state.collected] }; }
